import { useCallback, useMemo } from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { User } from "@common/types/user";
import { ServiceTemplateRecurrenceOccurrenceStatus } from "@common/types/serviceTemplateRecurrenceOccurrence";
import { Picker } from "@design-system/Picker/Picker.lib";
import { UserAvatar } from "@components/avatar/UserAvatar";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { HStack } from "@design-system/HStack/HStack";
import { BreakableText } from "@design-system/BreakableText/BreakableText";
import { useViewTableMeta } from "@hooks/view/useViewTableMeta";
import { formatDuration } from "@design-system/TimePicker/TimePicker.lib";
import { ServiceTemplateScheduleOccurrenceRow } from "../serviceTemplateScheduleOccurrencesViewConfig";
import {
    ServiceTemplateScheduleOccurrenceCallbacks,
    ServiceTemplateScheduleOccurrenceRoleField,
    ServiceTemplateScheduleOccurrenceTableOptions,
} from "../serviceTemplateScheduleOccurrenceCallbacks";
import { getMemberLabel } from "../serviceTemplateScheduleMemberLabel";
import {
    getRoleEstimatedMinutes,
    ROLE_FIELD_TO_ASSIGNEE_ROLE,
} from "../templateEstimateStats";
import styles from "./ServiceTemplateScheduleOccurrenceMemberCell.module.scss";

interface MemberOption {
    user: Pick<
        User,
        "id" | "name" | "preferredName" | "avatar" | "iconColor" | "email"
    >;
}

const MemberDisplay = ({
    member,
    estimatedMinutes,
}: {
    member: MemberOption;
    estimatedMinutes?: number;
}) => (
    <HStack gap={8} align="center">
        <UserAvatar user={member.user} size="sm" aria-hidden />
        <BreakableText text={getMemberLabel(member)} showTooltip />
        {estimatedMinutes != null && estimatedMinutes > 0 ? (
            <span className={styles.estimate}>
                {formatDuration(estimatedMinutes)}
            </span>
        ) : null}
    </HStack>
);

interface Props {
    row: CellContext<
        ViewTableFeatures,
        ServiceTemplateScheduleOccurrenceRow,
        number | null
    >["row"];
    table: CellContext<
        ViewTableFeatures,
        ServiceTemplateScheduleOccurrenceRow,
        number | null
    >["table"];
    field: ServiceTemplateScheduleOccurrenceRoleField;
    addText: string;
    emptyLabel: string;
}

export const ServiceTemplateScheduleOccurrenceMemberCell = ({
    row,
    table,
    field,
    addText,
    emptyLabel,
}: Props) => {
    const { callbacks, options } = useViewTableMeta<
        ServiceTemplateScheduleOccurrenceCallbacks,
        ServiceTemplateScheduleOccurrenceTableOptions
    >(table);
    const occurrence = row.original;
    const memberId = occurrence[field] ?? null;
    const estimatedMinutes = getRoleEstimatedMinutes(
        options?.templateEstimateStats,
        ROLE_FIELD_TO_ASSIGNEE_ROLE[field],
    );

    const memberOptions: MemberOption[] = useMemo(
        () =>
            field === "clientId"
                ? (options?.workspaceMembers ?? [])
                : (options?.assignableMembers ?? []),
        [field, options?.assignableMembers, options?.workspaceMembers],
    );

    const selectedMember = useMemo(
        () =>
            memberId != null
                ? (memberOptions.find((m) => m.user.id === memberId) ?? null)
                : null,
        [memberId, memberOptions],
    );

    // Guards against passing a stale/foreign id (e.g. a member who lost
    // workspace access) to the Select as its value — mirrors
    // ApplyCloseEntityMemberCell/ApplyCloseEntityClientCell.
    const validMemberId = selectedMember ? memberId : null;

    const isEditable =
        occurrence.status ===
        ServiceTemplateRecurrenceOccurrenceStatus.SCHEDULED;

    const handleChange = useCallback(
        (id: number | null) => {
            if (!callbacks || !isEditable) {
                return;
            }
            void callbacks.onUpdateRoleOverride({
                occurrenceId: occurrence.id,
                field,
                value: id,
            });
        },
        [callbacks, isEditable, occurrence.id, field],
    );

    if (!isEditable) {
        if (selectedMember) {
            return (
                <ViewTableCell>
                    <MemberDisplay
                        member={selectedMember}
                        estimatedMinutes={estimatedMinutes}
                    />
                </ViewTableCell>
            );
        }

        if (estimatedMinutes > 0) {
            return (
                <ViewTableCell>
                    <HStack gap={8} align="center">
                        <span className="text-muted">Unassigned</span>
                        <span className={styles.estimate}>
                            {formatDuration(estimatedMinutes)}
                        </span>
                    </HStack>
                </ViewTableCell>
            );
        }

        return (
            <ViewTableCell>
                <span className="text-muted">Unassigned</span>
            </ViewTableCell>
        );
    }

    return (
        <ViewTableCell captureClicks>
            <Picker.Select<number>
                value={validMemberId}
                onValueChange={handleChange}
                disabled={options?.isMutatingOccurrence ?? false}
            >
                <Picker.CellContent
                    value={
                        selectedMember && (
                            <MemberDisplay
                                member={selectedMember}
                                estimatedMinutes={estimatedMinutes}
                            />
                        )
                    }
                    aria-label={
                        selectedMember
                            ? getMemberLabel(selectedMember)
                            : undefined
                    }
                    addText={
                        estimatedMinutes > 0
                            ? `${addText} · ${formatDuration(estimatedMinutes)}`
                            : addText
                    }
                    hideAddIcon
                />
                <Picker.Popup>
                    <Picker.List>
                        <Picker.Item
                            onSelect={() => {
                                if (validMemberId !== null) {
                                    handleChange(null);
                                }
                            }}
                        >
                            {emptyLabel}
                        </Picker.Item>
                        {memberOptions.map((member) => (
                            <Picker.Item
                                key={member.user.id}
                                value={member.user.id}
                                leading={
                                    <UserAvatar
                                        user={member.user}
                                        size="xs"
                                        aria-hidden
                                    />
                                }
                            >
                                {getMemberLabel(member)}
                            </Picker.Item>
                        ))}
                    </Picker.List>
                </Picker.Popup>
            </Picker.Select>
        </ViewTableCell>
    );
};
